import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import FolioRule from "@/components/FolioRule";
import InkRevealTitle from "@/components/InkRevealTitle";
import ScrollReveal from "@/components/ScrollReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArchiveEntryPage() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/archive/${id}`)
      .then((r) => setEntry(r.data))
      .catch(() => setEntry(null))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  return (
    <>
      <NavBar />
      <main
        className="bg-pearl text-tobacco min-h-screen pt-32 pb-24 md:pt-40 md:pb-36 relative overflow-hidden"
        data-testid="archive-entry"
      >
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-12">
          <Link
            to="/#archive"
            className="font-mono text-[10px] tracking-widest2 uppercase text-tobacco hover:text-gold transition-colors"
            data-testid="archive-back"
          >
            ← {t("archive.back")}
          </Link>

          {loading ? (
            <div className="mt-24 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/50">
              Loading…
            </div>
          ) : !entry ? (
            <div className="mt-24 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/50">
              Not found.
            </div>
          ) : (
            <article className="mt-12 md:mt-16">
              <FolioRule label={entry.category} className="mb-12" />

              <InkRevealTitle text={lang === "es" ? entry.title_es : entry.title_en} />

              <motion.div
                initial={{ opacity: 0, scaleX: 0 }}
                animate={{ opacity: 1, scaleX: 1 }}
                transition={{ duration: 1.6, delay: 2.0, ease: [0.65, 0, 0.35, 1] }}
                style={{ transformOrigin: "0% 50%" }}
                className="mt-10 h-px bg-gradient-to-r from-tobacco/60 via-gold/70 to-transparent"
              />

              {entry.cover_image ? (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 2.2, delay: 1.4, ease: [0.65, 0, 0.35, 1] }}
                  className="mt-14 relative aspect-[16/9] overflow-hidden"
                >
                  <img src={entry.cover_image} alt="" className="w-full h-full object-cover grayscale-[15%]" />
                  <div className="absolute inset-0 ring-1 ring-tobacco/20 mix-blend-multiply pointer-events-none" />
                </motion.div>
              ) : null}

              {/* Eyebrow stat row + handwritten note */}
              <div className="mt-14 grid md:grid-cols-12 gap-8 md:gap-12">
                <div className="md:col-span-3">
                  <div className="font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55">
                    Folio
                  </div>
                  <div className="mt-1 font-display italic text-2xl text-tobacco">
                    N° {(entry.id || "").slice(0, 3).toUpperCase()}
                  </div>
                </div>
                <div className="md:col-span-3">
                  <div className="font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55">
                    Recorded
                  </div>
                  <div className="mt-1 font-display italic text-2xl text-tobacco">
                    {new Date(entry.created_at).toLocaleDateString(lang === "es" ? "es-DO" : "en-US", {
                      year: "numeric",
                      month: "long",
                    })}
                  </div>
                </div>
                <div className="md:col-span-6 md:flex md:justify-end">
                  <div className="font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55">
                    {lang === "es" ? "Anotado a mano · despacio" : "Set down by hand · slowly"}
                  </div>
                </div>
              </div>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 2.0, delay: 2.4, ease: [0.65, 0, 0.35, 1] }}
                className="mt-16 font-display italic text-2xl md:text-4xl text-tobacco/90 leading-snug max-w-3xl border-l-2 border-gold pl-6"
              >
                {lang === "es" ? entry.excerpt_es : entry.excerpt_en}
              </motion.p>

              {/* Body — line-by-line scroll reveal */}
              <div className="mt-20 md:mt-28 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-2 md:pt-2 hidden md:block">
                  <div className="sticky top-32 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55 leading-loose">
                    {lang === "es" ? "Manuscrito" : "Manuscript"}
                    <br />
                    {entry.category}
                  </div>
                </div>
                <div className="md:col-span-9 md:col-start-3 max-w-[680px]">
                  <ScrollReveal text={lang === "es" ? entry.body_es : entry.body_en} tone="tobacco" />

                  <div className="mt-16 flex items-center gap-6">
                    <div className="flex-1 luxury-rule" />
                    <span className="font-mono text-[10px] tracking-widest2 uppercase text-gold">
                      Fin · {lang === "es" ? "Folio cerrado" : "Folio closed"}
                    </span>
                    <div className="flex-1 luxury-rule" />
                  </div>

                  <div className="mt-14">
                    <Link
                      to="/#archive"
                      className="btn-luxe text-tobacco border-tobacco/40 hover:bg-tobacco/5"
                      data-testid="archive-back-bottom"
                    >
                      ← {t("archive.back")}
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
